import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiHomeAddressGetInput,
  type EnrollmentMultiHomeAddressData,
  type EnrollmentMultiHomeAddressSubmitInput,
  type EnrollmentMultiHomeAddressSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import {
  getEnrollmentMultiHomeAddressDataQuery,
  submitEnrollmentMultiHomeAddressDataQuery,
} from './queries'

/**
 * Get Enrollment Multi HomeAddress data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiHomeAddressData>
 * @example const response = await getEnrollmentMultiHomeAddressData()
 */
export const getEnrollmentMultiHomeAddressData = async (
  enrollmentHomeAddressGetInput?: EnrollmentMultiHomeAddressGetInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiHomeAddressData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiHomeAddressData',
      query: getEnrollmentMultiHomeAddressDataQuery,
    },
    {
      submitEnrollmentInput: enrollmentHomeAddressGetInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiHomeAddressData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiHomeAddressData = createQueryHook<
  EnrollmentMultiHomeAddressGetInput,
  EnrollmentMultiHomeAddressData
>(getEnrollmentMultiHomeAddressData)

/**
 * Submit Enrollment Multi HomeAddress data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiHomeAddressSubmitOutput>
 * @example const response = await submitEnrollmentMultiHomeAddressData();
 */
export const submitEnrollmentMultiHomeAddressData = async (
  enrollmentHomeAddressSubmitInput: EnrollmentMultiHomeAddressSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiHomeAddressSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiHomeAddressData',
      query: submitEnrollmentMultiHomeAddressDataQuery,
    },
    {
      submitEnrollmentInput: enrollmentHomeAddressSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiHomeAddressSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiHomeAddressData = createMutationHook<
  EnrollmentMultiHomeAddressSubmitInput,
  EnrollmentMultiHomeAddressSubmitOutput
>(submitEnrollmentMultiHomeAddressData)
